import request from "@/utils/request";

export interface SendMobileCodeRequest {
	/**
	 * 手机号
	 */
	mobile: string;
}

/**
 * 发送手机验证码
 * @param data 请求数据
 * @returns 成功返回验证码发送结果字符串
 */
export const sendMobileCode = (data: SendMobileCodeRequest) =>
	request.post<string>("/common/sendMobile", data);

export interface IndustryClassificationDatumSecondChild {
	id: number;
	/**
	 * 行业名称
	 */
	name: string;
	/**
	 * 行业父级id
	 */
	pid: number;
}

export interface IndustryClassificationDatumChild {
	children?: IndustryClassificationDatumSecondChild[];
	/**
	 * 行业二级id
	 */
	id: number;
	/**
	 * 行业名称
	 */
	name: string;
	/**
	 * 行业父级id
	 */
	pid: number;
}

export interface IndustryClassificationDatum {
	/**
	 * 行业二级信息
	 */
	children: IndustryClassificationDatumChild[];
	/**
	 * 行业一级id
	 */
	id: number;
	/**
	 * 行业名称
	 */
	name: string;
	/**
	 * 行业父级id
	 */
	pid: number;
}

export type FetchIndustryClassificationResponse = Array<IndustryClassificationDatum>;

/**
 * 获取行业分类列表
 * @returns 行业分类列表
 */
export const fetchIndustryClassification = () =>
	request.post<FetchIndustryClassificationResponse>("/common/industry");

export interface OrganizationalClassificationDatumSecondChild {
	id: number;
	name: string;
	pid: number;
}

export interface OrganizationalClassificationDatumChild {
	children?: OrganizationalClassificationDatumSecondChild[];
	/**
	 * 机构组织二级id
	 */
	id: number;
	/**
	 * 机构组织名称
	 */
	name: string;
	/**
	 * 机构组织父级id
	 */
	pid: number;
}

export interface OrganizationalClassificationDatum {
	/**
	 * 机构组织二级信息
	 */
	children: OrganizationalClassificationDatumChild[];
	/**
	 * 机构组织一级id
	 */
	id: number;
	/**
	 * 机构组织名称
	 */
	name: string;
	/**
	 * 机构组织父级id
	 */
	pid: number;
}

export type FetchOrganizationalClassificationResponse = Array<OrganizationalClassificationDatum>;

/**
 * 获取组织分类列表
 * @returns 机构分类列表
 */
export const fetchOrganizationalClassification = () =>
	request.post<FetchOrganizationalClassificationResponse>("/common/institution");

export enum ActionTypeEnum {
	OrderLogExport = "订单导出",
	RechargeImport = "充值余额导入",
	AdminImport = "成员导入",
	DeptImport = "部门导入",
	EmployeeImport = "花名册导入",
}

export type ImportActionType =
	/**
	 * 充值余额导入
	 */
	| "RechargeImport"
	/**
	 * 成员导入
	 */
	| "AdminImport"
	/**
	 * 部门导入
	 */
	| "DeptImport"
	/**
	 * 花名册导入
	 */
	| "EmployeeImport";

export interface CreateImportTaskRequest {
	/**
	 * 导入子任务名称
	 */
	action: keyof typeof ActionTypeEnum;
	/**
	 * oss的xlsx格式文件路径
	 */
	oss_url: string;
}

/**
 * 创建导入任务
 */
export const createImportTask = (data: CreateImportTaskRequest) =>
	request.post("/common/import", data);

export interface FetchImportTaskListRequest extends PaginationParams {
	/**
	 * 子任务类型：OrderLogExport-订单导出；RechargeImport-充值余额导入
	 */
	action?: keyof typeof ActionTypeEnum;
	/**
	 * 任务关键词
	 */
	keyword?: string;
	/**
	 * 任务处理状态：0-待处理 1-处理中 2-处理成功 3-处理失败 4-冻结
	 */
	status?: number;
	/**
	 * 任务类型：1-导入 2-导出
	 */
	type?: number;
}

export interface ImportTaskListItem {
	/**
	 * 子任务类型：OrderLogExport-订单导出；RechargeImport-充值余额导入
	 */
	action: keyof typeof ActionTypeEnum;
	/**
	 * 后台用户id
	 */
	admin_id: number;
	/**
	 * 企业编码id
	 */
	association_id: number;
	/**
	 * 创建时间
	 */
	create_at: string;
	/**
	 * 失败的条数
	 */
	fail?: number;
	/**
	 * 失败的文件地址，用户导入失败文件下载查看具体错误信息
	 */
	fail_url?: string;
	/**
	 * 删除状态：0-否 1-是
	 */
	is_del: number;
	/**
	 * 本地文件url
	 */
	local_url: string;
	/**
	 * 任务名称
	 */
	name: string;
	/**
	 * 源文件
	 */
	oss_url: string;
	/**
	 * 执行条件
	 */
	params: string;
	/**
	 * 执行任务失败，备注信息
	 */
	remark: string;
	/**
	 * 任务处理状态：0-待处理 1-处理中 2-处理成功 3-处理失败 4-冻结
	 */
	status: number;
	/**
	 * 成功条数
	 */
	success?: number;
	/**
	 * 任务id
	 */
	task_id: number;
	/**
	 * 数据总条数
	 */
	total: number;
	/**
	 * 任务类型：1-导入 2-导出
	 */
	type: number;
	/**
	 * 更新时间
	 */
	update_at: string;
	/**
	 * 操作人
	 */
	operator: string;
}

export type FetchImportTaskListResponse = ListDataResponse<ImportTaskListItem>;

export const fetchImportTaskList = (data: FetchImportTaskListRequest) =>
	request.post<FetchImportTaskListResponse>("/common/taskList", data);

/**
 * 导出数据查询参数
 */
export interface JsonParams {
	/**
	 * 导出的动态表头字段，格式例如：["order_id","amount","pay_amount",]
	 */
	field?: string[];
	/**
	 * 订单编码，例如：["202512251942560691","202512251942560692"]
	 */
	order_id?: string[];
	/**
	 * 0-待付款 1-已完成 2-已发货 3-待发货 4-已取消 5-售后中，例如：[0,1]
	 */
	order_status?: number[];
	/**
	 * 支付状态：状态：0-未付款 1-已付款 2-有部分退款 3-全部退款，格式例如：[1,2]
	 */
	pay_status?: number[];
	/**
	 * 支付方式：1-微信 2-支付宝
	 */
	pay_type?: number;
	/**
	 * 订单创建开始时间，例如：2025-12-01
	 */
	start_at?: string;
	/**
	 * 订单创建截止时间，例如：2025-12-05
	 */
	start_end?: string;
	user_id?: string[];
	keyword?: string;
}

export interface CreateExportTaskRequest {
	/**
	 * 导出子任务名称
	 */
	action: keyof typeof ActionTypeEnum;
	/**
	 * 导出数据查询参数
	 */
	json_params: JsonParams;
}

export const createExportTask = (data: CreateExportTaskRequest) =>
	request.post("/common/export", data);

export interface ExpressItem {
	/**
	 * 物流公司英文简称
	 */
	brief_introduction: string;
	/**
	 * 物流公司id
	 */
	id: number;
	/**
	 * 物流公司中文名称
	 */
	name: string;
}

export type FetchExpressListResponse = Array<ExpressItem>;

export const fetchExpressList = () => request.post<FetchExpressListResponse>("/common/express");

export interface ImportExportTemplateResponse {
	file_url: string;
}

export interface ImportExportTemplateRequest {
	/**
	 * 模版模块：employee-花名册
	 */
	action: string;
}

export const importExportTemplate = (data: ImportExportTemplateRequest) =>
	request.post<ImportExportTemplateResponse>("/common/importTemplate", data);

export interface PrimaryAdmin {
	/**
	 * 工号
	 */
	admin_sn: string;
	/**
	 * 邮箱
	 */
	email: string;
	/**
	 * 成员id
	 */
	admin_id: number;
	/**
	 * 手机号
	 */
	mobile: string;
	/**
	 * 真实姓名
	 */
	realname: string;
}

export const fetchPrimaryAdmins = () => request.post<Array<PrimaryAdmin>>("/common/primaryAdmins");

export interface CommonAdminItem {
	/**
	 * 工号
	 */
	admin_sn: string;
	/**
	 * 企业id
	 */
	association_id: number;
	/**
	 * 部门id
	 */
	dept_id: number;
	/**
	 * 邮箱
	 */
	email: string;
	/**
	 * 成员id
	 */
	id: number;
	/**
	 * 是否主任部门：0-否；1-是
	 */
	is_primary: number;
	/**
	 * 主管id
	 */
	leader_id: number;
	/**
	 * 手机号
	 */
	mobile: string;
	/**
	 * 职位id
	 */
	position_id: number;
	/**
	 * 真实姓名
	 */
	realname: string;
	/**
	 * 状态
	 */
	status: string;
}

export interface CommonAdminListItem {
	/**
	 * 成员列表
	 */
	admin_list: CommonAdminItem[];
	children: CommonAdminListItem[];
	/**
	 * 部门id
	 */
	dept_id: number;
	/**
	 * 部门名称
	 */
	dept_name: string;
	/**
	 * 介绍
	 */
	instro: string;
	/**
	 * 部门主管
	 */
	leader_ids: string;
	/**
	 * 上级id
	 */
	pid: number;
	/**
	 * 部门类型：1-内部企业部门；2-关联外部企业部门
	 */
	type: number;
}

export const fetchCommonAdminList = () => request.post<CommonAdminListItem[]>("/common/adminList");

export interface CommonRoleListItem {
	/**
	 * 角色分组id
	 */
	group_id: number;
	/**
	 * 角色id
	 */
	id: number;
	/**
	 * 角色名称
	 */
	name: string;
	/**
	 * 是否系统内置角色：0-否；1-是
	 * 系统内置角色不允许编辑和删除
	 */
	is_system: number;
}

export interface CommonRoleGroupListItem {
	/**
	 * 角色分组id
	 */
	group_id: number;
	/**
	 * 角色分组名称
	 */
	group_name: string;
	/**
	 * 角色list
	 */
	list: CommonRoleListItem[];
	/**
	 * 是否系统内置角色：0-否；1-是
	 * 系统内置角色不允许编辑和删除
	 */
	is_system: number;
}

export const fetchCommonRoleGroupList = () =>
	request.post<CommonRoleGroupListItem[]>("/common/roleGroupList");

export interface CommonPositionItem {
	/**
	 * 职位id
	 */
	position_id: number;
	/**
	 * 职位名称
	 */
	position_name: string;
	/**
	 * 职位简介
	 */
	instro: string;
}

export const fetchCommonPositionList = (keyword?: string) =>
	request.post<CommonPositionItem[]>("/common/positionList", { keyword });

export interface CommonDepartmentalChild {
	/**
	 * 部门id
	 */
	dept_id: number;
	/**
	 * 部门名称
	 */
	dept_name: string;
	/**
	 * 部门简介
	 */
	instro: string;
	/**
	 * 主管ID集合，例如：[1001,10002]
	 */
	leader_ids: number[];
	/**
	 * 部门人数
	 */
	persons_num: number;
	/**
	 * 上级部门id
	 */
	pid: number;
	/**
	 * 部门类型：1-内部企业部门；2-关联外部企业部门
	 */
	type: number;
	/**
	 * 下级部门列表
	 */
	children: CommonDepartmentalChild[];
	/**
	 * 是否是系统内置部门
	 * :0-否(可删除);1-是
	 */
	is_system: number;
}

export type FetchCommonDepartmentalListResponse = CommonDepartmentalChild[];

/**
 * 部门-列表
 */
export const fetchCommonDepartmentalList = () =>
	request.post<FetchCommonDepartmentalListResponse>("/common/deptList");
